import { CircularProgress } from '@mui/material';
import { LicenseDto } from '@thingslog/repositories';
import { LicenseWarningBox } from '@thingslog/ui-components';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { licenseQueryClient } from '../../utils/ReactQueryClients';
import { PageHeader, PortalPageShell } from '../../components';
import { useCurrentCompanyId } from '../../hooks';

const LicenseError: React.FC<LicenseErrorProps> = (props: LicenseErrorProps) => {
  const { t } = useTranslation();
  const { useLicenses } = useMemo(() => licenseQueryClient, []);
  const companyId = useCurrentCompanyId();

  const [foundInactiveLicense, setFoundInactiveLicense] = useState<boolean>(false);
  const [invalidLicenses, setInvalidLicenses] = useState<boolean>(false);

  const { isLoading } = useLicenses(companyId ?? null, undefined, undefined, {
    onSuccess: (data: LicenseDto[]) => {
      let hasInactiveLicense = data.some((license: LicenseDto) => !license.isActive);
      let hasAtLeastOneValidLicense = data.some((license: LicenseDto) => license.isValid);
      setFoundInactiveLicense(hasInactiveLicense);
      setInvalidLicenses(!hasAtLeastOneValidLicense);
    },
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <PageHeader title="" options={{ showLogoutButton: true }} />
      {isLoading ? (
        <PortalPageShell className="flex items-center justify-center">
          <CircularProgress size="2.5rem" />
        </PortalPageShell>
      ) : (
        <PortalPageShell maxWidth="form">
          <h1 className="text-center text-xl font-bold text-gray-900">
            {t('license_error_page_access_restricted')}
          </h1>
          <div className="flex flex-col items-center justify-center text-justify">
            <LicenseWarningBox
              warningStatus={{
                noActiveLicense: foundInactiveLicense,
                noValidLicense: invalidLicenses,
                devicesCountLimitReached: false,
              }}
              translation={{
                accessOnHoldTitle: t('license_warning_access_hold'),
                accessOnHoldDescription: t('license_warning_access_hold_description'),
                noValidLicenseTitle: t('license_warning_no_valid_license'),
                noValidLicenseDescription: t('license_warning_no_valid_license_description'),
                deviceLimitReachedTitle: t('license_warning_devices_count_limit_reached'),
                deviceLimitReachedDescription: t(
                  'license_warning_devices_count_limit_reached_description'
                ),
                defaultGenericTitle: t('license_warning_generic_title'),
                defaultGenericDescription: t('license_warning_generic_description'),
              }}
            />
          </div>
        </PortalPageShell>
      )}
    </>
  );
};

interface LicenseErrorProps {}

export default LicenseError;
